import { useEffect, useRef } from 'react';

const COLORS = [
    [251, 250, 249],
    [251, 250, 249],
    [251, 250, 249],
    [251, 250, 249],
    [251, 250, 249],
    [251, 250, 249],
    [251, 250, 249],
    [251, 250, 249],
    [0, 108, 236],
    [236, 128, 0],
    [247, 184, 1],
    [251, 250, 249],
    [251, 250, 249],
    [251, 250, 249],
    [251, 250, 249],
    [251, 250, 249],
    [251, 250, 249],
    [251, 250, 249],
];

const PERM = new Uint8Array(512);
const GRAD3 = [
    [1,1,0],[-1,1,0],[1,-1,0],[-1,-1,0],
    [1,0,1],[-1,0,1],[1,0,-1],[-1,0,-1],
    [0,1,1],[0,-1,1],[0,1,-1],[0,-1,-1],
];

(function initPerm() {
    const p = [];
    for (let i = 0; i < 256; i++) p[i] = i;
    for (let i = 255; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [p[i], p[j]] = [p[j], p[i]];
    }
    for (let i = 0; i < 512; i++) PERM[i] = p[i & 255];
})();

function dot3(g, x, y, z) {
    return g[0] * x + g[1] * y + g[2] * z;
}

function simplex3(x, y, z) {
    const F3 = 1 / 3, G3 = 1 / 6;
    const s = (x + y + z) * F3;
    const i = Math.floor(x + s), j = Math.floor(y + s), k = Math.floor(z + s);
    const t = (i + j + k) * G3;
    const X0 = i - t, Y0 = j - t, Z0 = k - t;
    const x0 = x - X0, y0 = y - Y0, z0 = z - Z0;

    let i1, j1, k1, i2, j2, k2;
    if (x0 >= y0) {
        if (y0 >= z0) { i1=1;j1=0;k1=0;i2=1;j2=1;k2=0; }
        else if (x0 >= z0) { i1=1;j1=0;k1=0;i2=1;j2=0;k2=1; }
        else { i1=0;j1=0;k1=1;i2=1;j2=0;k2=1; }
    } else {
        if (y0 < z0) { i1=0;j1=0;k1=1;i2=0;j2=1;k2=1; }
        else if (x0 < z0) { i1=0;j1=1;k1=0;i2=0;j2=1;k2=1; }
        else { i1=0;j1=1;k1=0;i2=1;j2=1;k2=0; }
    }

    const x1 = x0-i1+G3, y1 = y0-j1+G3, z1 = z0-k1+G3;
    const x2 = x0-i2+2*G3, y2 = y0-j2+2*G3, z2 = z0-k2+2*G3;
    const x3 = x0-1+3*G3, y3 = y0-1+3*G3, z3 = z0-1+3*G3;

    const ii = i & 255, jj = j & 255, kk = k & 255;

    let n = 0;
    let c = 0.6 - x0*x0 - y0*y0 - z0*z0;
    if (c > 0) { c *= c; n += c * c * dot3(GRAD3[PERM[ii+PERM[jj+PERM[kk]]] % 12], x0, y0, z0); }
    c = 0.6 - x1*x1 - y1*y1 - z1*z1;
    if (c > 0) { c *= c; n += c * c * dot3(GRAD3[PERM[ii+i1+PERM[jj+j1+PERM[kk+k1]]] % 12], x1, y1, z1); }
    c = 0.6 - x2*x2 - y2*y2 - z2*z2;
    if (c > 0) { c *= c; n += c * c * dot3(GRAD3[PERM[ii+i2+PERM[jj+j2+PERM[kk+k2]]] % 12], x2, y2, z2); }
    c = 0.6 - x3*x3 - y3*y3 - z3*z3;
    if (c > 0) { c *= c; n += c * c * dot3(GRAD3[PERM[ii+1+PERM[jj+1+PERM[kk+1]]] % 12], x3, y3, z3); }

    return 32 * n;
}

function lerpColor(colors, t) {
    const idx = t * (colors.length - 1);
    const i = Math.floor(idx);
    const f = idx - i;
    const c0 = colors[Math.min(i, colors.length - 1)];
    const c1 = colors[Math.min(i + 1, colors.length - 1)];
    return [
        c0[0] + (c1[0] - c0[0]) * f,
        c0[1] + (c1[1] - c0[1]) * f,
        c0[2] + (c1[2] - c0[2]) * f,
    ];
}

const FlowingGradient = () => {
    const canvasRef = useRef(null);
    const animRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const W = 96;
        const H = 54;
        canvas.width = W;
        canvas.height = H;
        const imgData = ctx.createImageData(W, H);

        const startTime = performance.now();
        const seed = Math.random() * 100;
        const L = 0.0008;
        const S = 0.13;
        const F = 0.11;
        const Y_SCALE = 2.0;
        const scaleX = window.innerWidth / W;
        const scaleY = window.innerHeight / H;

        const draw = () => {
            const time = (performance.now() - startTime) / 1000 + seed;
            const data = imgData.data;

            for (let py = 0; py < H; py++) {
                const realY = py * scaleY * Y_SCALE;
                for (let px = 0; px < W; px++) {
                    const realX = px * scaleX;

                    let noise = 0.5;
                    noise += simplex3(realX * L, realY * L, time * S) * 0.30;
                    noise += simplex3(realX * L * 0.6 + F * time, realY * L * 0.85, time * S) * 0.26;
                    noise += simplex3(realX * L * 0.4 - F * time * 0.6, realY * L * 0.70, time * S) * 0.22;

                    let t = Math.max(0, Math.min(1, noise));
                    t = Math.pow(t, 1.2);

                    const [r, g, b] = lerpColor(COLORS, t);
                    const idx = (py * W + px) * 4;
                    data[idx] = r;
                    data[idx + 1] = g;
                    data[idx + 2] = b;
                    data[idx + 3] = 255;
                }
            }

            ctx.putImageData(imgData, 0, 0);
            animRef.current = requestAnimationFrame(draw);
        };

        animRef.current = requestAnimationFrame(draw);
        return () => cancelAnimationFrame(animRef.current);
    }, []);

    return (
        <div className="fixed inset-0 z-0 pointer-events-none" style={{ height: '100lvh', width: '100vw' }}>
            <div className="absolute inset-0 opacity-50 blur-[12px] will-change-auto 2xl:blur-[48px]">
                <canvas
                    ref={canvasRef}
                    className="absolute inset-0 h-full w-full"
                />
            </div>
        </div>
    );
};

export default FlowingGradient;
